import React, { memo, useEffect, useRef, useState } from 'react';
import API_BASE_URL from '../../../../js/urlHelper';
import { fetchWithAuth } from '../../../../js/authToken';

const AdvisorSearch = ({ selectedAdvisor, onSelect, onRemove, errors, apiBasePath = '/api/admin' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [advisors, setAdvisors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    // Close dropdown when clicking outside
    function handleClickOutside(event) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update search term when a selected advisor changes
  useEffect(() => {
    if (selectedAdvisor) {
      const advisorName = `${selectedAdvisor.datos?.nombre || ''} ${selectedAdvisor.datos?.apellidoPaterno || ''} ${selectedAdvisor.datos?.apellidoMaterno || ''} 
      ${selectedAdvisor.datos?.apellidoConyuge || ''}`.trim();
      setSearchTerm(advisorName);
    } else {
      setSearchTerm('');
    }
  }, [selectedAdvisor]);

  useEffect(() => {
    // Only search if there's a term and it's at least 2 characters
    if (searchTerm.length >= 2 && !selectedAdvisor) {
      searchAdvisors();
    } else if (!selectedAdvisor) {
      setAdvisors([]);
      setShowResults(searchTerm.length >= 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedAdvisor]);

  const searchAdvisors = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}${apiBasePath}/calculadora/asesores?search=${encodeURIComponent(searchTerm)}`);
      if (response.ok) {
        const data = await response.json();
        setAdvisors(data.asesores || []);
        setShowResults(true);
      } else {
        console.error('Error fetching advisors');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAdvisor = (advisor) => {
    onSelect(advisor);
    setShowResults(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (selectedAdvisor) {
      // If there's a selected advisor but the user is typing, clear the selection
      onRemove();
    }
  };

  const handleInputFocus = () => {
    if (searchTerm.length >= 2) {
      setShowResults(true);
    }
  };

  return (
    <div className="relative" ref={searchContainerRef}>
      {!selectedAdvisor ? (
        // Mostrar input de búsqueda cuando no hay asesor seleccionado
        <div className="flex flex-col">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder="BUSCAR ASESOR..."
              className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors ? 'border-red-500' : 'border-slate-300'
                }`}
            />
          </div>
        </div>
      ) : (
        // Mostrar asesor seleccionado
        <div className="flex items-center justify-between rounded-xl border border-slate-300 bg-white p-2">
          <div className="text-sm">
            <span className="font-medium">ASESOR SELECCIONADO: </span>
            <span>{selectedAdvisor.datos?.nombre || ''} {selectedAdvisor.datos?.apellidoPaterno || ''} {selectedAdvisor.datos?.apellidoMaterno || ''} {selectedAdvisor.datos?.apellidoConyuge || ''}</span>
            {selectedAdvisor.datos?.dni && <span className="ml-1 text-slate-500">- {selectedAdvisor.datos.dni}</span>}
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="px-2 text-lg font-bold text-red-500 transition hover:text-red-700"
            aria-label="Remover asesor"
          >
            ×
          </button>
        </div>
      )}

      {/* Search results */}
      {showResults && !selectedAdvisor && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {isLoading ? (
            <div className="p-2 text-center text-slate-500">CARGANDO...</div>
          ) : advisors.length > 0 ? (
            <ul>
              {advisors.map((advisor) => (
                <li
                  key={advisor.id || `advisor-${advisor.datos?.dni || Math.random()}`}
                  className="cursor-pointer px-3 py-2 text-sm transition hover:bg-red-50"
                  onClick={() => handleSelectAdvisor(advisor)}
                >
                  {advisor.datos?.nombre || ''} {advisor.datos?.apellidoPaterno || ''} {advisor.datos?.apellidoMaterno || ''}{advisor.datos?.apellidoConyuge ? ` ${advisor.datos.apellidoConyuge}` : ''}{advisor.datos?.dni ? ` - ${advisor.datos.dni}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-2 text-center text-slate-500">NO SE ENCONTRARON ASESORES</div>
          )}
        </div>
      )}

      {errors && <p className="text-red-500 text-sm mt-1">{errors}</p>}
    </div>
  );
};

export default memo(AdvisorSearch);
